import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusyOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { topN } from '@/lib/dashboardAggregations';
import type { AbcAnalysisRow, InventoryValuationRow, LowStockRow, NearExpiryRow } from '@/types/reports';

const formatCurrency = (v: number) => `₹${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function InventorySlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: valuation = [] } = useQuery({
    queryKey: ['reports', 'inventory-valuation'],
    queryFn: async () => (await apiClient.get<InventoryValuationRow[]>('/reports/inventory-valuation')).data,
    refetchInterval: 60_000,
  });
  const { data: lowStock = [] } = useQuery({
    queryKey: ['reports', 'low-stock'],
    queryFn: async () => (await apiClient.get<LowStockRow[]>('/reports/low-stock')).data,
    refetchInterval: 60_000,
  });
  const { data: nearExpiry = [] } = useQuery({
    queryKey: ['reports', 'near-expiry'],
    queryFn: async () => (await apiClient.get<NearExpiryRow[]>('/reports/near-expiry', { params: { days: 30 } })).data,
    refetchInterval: 60_000,
  });
  const { data: abcAnalysis = [] } = useQuery({
    queryKey: ['reports', 'abc-analysis'],
    queryFn: async () => (await apiClient.get<AbcAnalysisRow[]>('/reports/abc-analysis')).data,
    refetchInterval: 60_000,
  });

  const totalStockValue = useMemo(() => valuation.reduce((sum, r) => sum + r.stockValue, 0), [valuation]);
  const topMaterials = useMemo(() => topN(valuation, (r) => r.stockValue, 5), [valuation]);
  const abcColors = [colors.inventory, colors.production, colors.adjustments];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('inventory.totalStockValue')} value={formatCurrency(totalStockValue)} icon={<PaymentsIcon />} accentColor={colors.inventory} />
        <TvKpiTile label={t('inventory.skuCount')} value={valuation.length} icon={<Inventory2Icon />} accentColor={colors.inventory} />
        <TvKpiTile label={t('lowStock')} value={lowStock.length} icon={<WarningAmberIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('nearExpiry')} value={nearExpiry.length} icon={<EventBusyIcon />} accentColor={colors.adjustments} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('inventory.abcTitle')} subtitle={t('inventory.abcSubtitle')} height={420}>
          <DashboardPieChart
            height={420}
            data={abcAnalysis.map((r, i) => ({ label: `${tr('columns.abcClass')} ${r.abcClass}`, value: r.stockValue, color: abcColors[i % abcColors.length] }))}
            valueFormatter={(item) => formatCurrency(item.value)}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList
          title={t('inventory.topMaterialsTitle')}
          accentColor={colors.inventory}
          items={topMaterials.map((r) => ({ label: `${r.materialCode} — ${r.materialName}`, value: r.stockValue, formattedValue: formatCurrency(r.stockValue) }))}
        />
      </Box>
    </Box>
  );
}
